import React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { House, FolderOpen, BookBookmark, GearSix, SignOut, Ruler } from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Projects", icon: FolderOpen, testid: "nav-projects" },
  { to: "/pricebook", label: "Price Book", icon: BookBookmark, testid: "nav-pricebook" },
  { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
];

export default function AppShell() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-950 text-ink-400">
        <div className="data-label">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-950 text-ink-50 flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-ink-700 bg-ink-900 flex flex-col" data-testid="app-sidebar">
        <Link to="/dashboard" className="flex items-center gap-3 p-5 border-b border-ink-700">
          <div className="h-9 w-9 rounded-md bg-amber flex items-center justify-center">
            <Ruler size={20} weight="bold" className="text-ink-950" />
          </div>
          <div>
            <div className="font-heading font-bold tracking-tight text-base">JOINER</div>
            <div className="data-label">Estimator</div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-ink-800 text-ink-50 border border-ink-700"
                    : "text-ink-400 hover:text-ink-50 hover:bg-ink-800 border border-transparent"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-ink-700 p-3">
          <div className="flex items-center gap-3 p-2 rounded-md">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-ink-800 border border-ink-700 flex items-center justify-center text-xs font-semibold text-ink-400">
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-ink-500 truncate">{user.email}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              title="Sign out"
              className="text-ink-400 hover:text-amber transition-colors duration-150"
            >
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
